import type { ReactElement } from 'react';
import { cn, useI18n } from '@xdocs/ui';
import styles from './PasswordStrengthMeter.module.css';

export type StrengthScore = 0 | 1 | 2 | 3 | 4;

/** Heuristic 0–4 score mirroring `signupInput`'s policy shape (8+ chars,
 * number, symbol) plus length/case bonuses — client-side feedback only, the
 * server-validated schema stays the real gate. */
export function scorePassword(password: string): StrengthScore {
  if (password.length === 0) return 0;
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  return Math.min(score, 4) as StrengthScore;
}

const LEVEL_LABEL = {
  1: 'auth:signup.strength.weak',
  2: 'auth:signup.strength.fair',
  3: 'auth:signup.strength.good',
  4: 'auth:signup.strength.strong',
} as const;

const LEVEL_CLASS = { 1: 'weak', 2: 'fair', 3: 'good', 4: 'strong' } as const;

/** Four-segment strength bar + label under the signup password field.
 * Renders nothing until the user types (an empty untouched field shouldn't
 * scold, per the forms rule "never validate an untouched field"). */
export function PasswordStrengthMeter({ password }: { password: string }): ReactElement | null {
  const { t } = useI18n();
  if (password.length === 0) return null;
  const score = Math.max(scorePassword(password), 1) as 1 | 2 | 3 | 4;
  return (
    <div className={cn(styles['meter'])}>
      <div className={cn(styles['track'])} aria-hidden="true">
        {([1, 2, 3, 4] as const).map((segment) => (
          <span
            key={segment}
            className={cn(
              styles['segment'],
              segment <= score && styles['filled'],
              segment <= score && styles[LEVEL_CLASS[score]],
            )}
          />
        ))}
      </div>
      <p className={cn(styles['label'])} aria-live="polite">
        {t('auth:signup.strength.label')}: <strong>{t(LEVEL_LABEL[score])}</strong>
      </p>
    </div>
  );
}
